import { Component, inject, input, OnInit, ViewContainerRef } from '@angular/core';

/**
 * Angular component that wraps a web component definition and mounts it to the view. Note that
 * inputs are not reactive.
 */
@Component({
  standalone: true,
  selector: 'web-component',
  template: '',
})
export class WebComponent implements OnInit {
  /**
   * One of:
   *
   * - The web component class definition.
   * - A string to the location of a module to import containing the web component class definition as
   *   its default export.
   */
  readonly webComponent = input.required<new () => HTMLElement | string>();

  /**
   * A unique string to use in the element tag name. If omitted, a random string is generated and
   * used.
   *
   * See
   * https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/define#valid_custom_element_names
   */
  readonly tagId = input<string>();

  /**
   * Data to pass to the element.
   *
   * Within the custom element class it will be accessible via `this.data`.
   */
  readonly data = input<unknown>();

  private readonly host = inject(ViewContainerRef).element.nativeElement;

  async ngOnInit() {
    const webComponent = this.webComponent();

    const resolvedWebComponent =
      typeof webComponent === 'string'
        ? (await import(/* @vite-ignore */ webComponent)).default
        : webComponent;

    const tagId =
      this.tagId() ??
      String.fromCharCode(...Array.from({ length: 10 }, () => Math.random() * 26 + 97));

    const tagName = `custom-${tagId}`;

    if (!customElements.get(tagName)) {
      customElements.define(tagName, resolvedWebComponent);
    }

    const element = document.createElement(tagName) as HTMLElement & { data: unknown };

    element.data = this.data();

    this.host.appendChild(element);
  }
}
